import { useMemo, useState } from "react"
import { Button, Stack, TextField, Typography } from "@mui/material"
import { AutocompleteInput } from "../ui-kit/AutocompleteInput"
import modalEmitter from "../ModalComponent/modalEmitter"
import {
  useCreateProjectInvitationMutation,
  useGetInvitableProjectsQuery,
} from "../../api/projects"
import {
  useCreateTeamInvitationMutation,
  useGetInvitableTeamsQuery,
} from "../../api/teams"
import { toast } from "react-toastify"
import { extractApiErrorMessage } from "../../utils/api"

type InviteProjectModalBodyProps = {
  userId: number
  projectId?: number
  projectName?: string | null
  introText?: string
  messagePlaceholder?: string
  submitLabel?: string
  successMessage?: string
  onSuccess?: () => void | Promise<void>
}

export const InviteProjectModalBody = ({
  userId,
  projectId: fixedProjectId,
  projectName,
  introText = "Вы действительно хотите пригласить пользователя в проект?",
  messagePlaceholder = "Напишите кратко, зачем приглашаете",
  submitLabel = "Пригласить",
  successMessage = "Приглашение отправлено",
  onSuccess,
}: InviteProjectModalBodyProps) => {
  const { data: projects = [], isLoading: isProjectsLoading } = useGetInvitableProjectsQuery()
  const [createInvitation, { isLoading: isSubmitting }] = useCreateProjectInvitationMutation()
  const [selectedId, setSelectedId] = useState<string | number | null>(fixedProjectId ?? null)
  const [message, setMessage] = useState("")
  const hasFixedProject = fixedProjectId != null

  const projectOptions = useMemo(
    () =>
      projects.map((p) => ({
        label: typeof p.name === "string" && p.name.trim().length > 0 ? p.name : `Проект #${p.id}`,
        value: p.id,
      })),
    [projects]
  )

  const handleConfirm = async () => {
    if (selectedId == null || selectedId === "") return
    const projectId = Number(selectedId)
    if (!Number.isFinite(projectId) || projectId <= 0) return

    const trimmed = message.trim()
    try {
      await createInvitation({
        projectId,
        inviteeId: userId,
        ...(trimmed.length > 0 ? { message: trimmed } : {}),
      }).unwrap()
      await onSuccess?.()
      toast.success(successMessage)
      modalEmitter.emitCloseModal()
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? "Не удалось отправить приглашение")
    }
  }

  const loading = (!hasFixedProject && isProjectsLoading) || isSubmitting

  return (
    <Stack gap={2}>
      <Typography>{introText}</Typography>
      {hasFixedProject ? (
        <Stack gap={0.5}>
          <Typography variant="black-14-light">Проект</Typography>
          <Typography>{projectName || `Проект #${fixedProjectId}`}</Typography>
        </Stack>
      ) : (
        <AutocompleteInput
          options={projectOptions}
          value={selectedId ?? ""}
          onChange={(v) => setSelectedId(v ?? null)}
          placeholder="Выберите..."
          fullWidth
          disabled={loading || projectOptions.length === 0}
        />
      )}
      <Typography variant="black-14-light">Сообщение (необязательно)</Typography>
      <TextField
        placeholder={messagePlaceholder}
        multiline
        minRows={3}
        value={message}
        onChange={(e) => setMessage(e.target.value.slice(0, 500))}
        helperText={`${message.length}/500`}
        disabled={loading}
        fullWidth
      />
      <Stack direction="row" gap={2} justifyContent="flex-end">
        <Button
          variant="action"
          disabled={selectedId == null || selectedId === "" || loading}
          onClick={handleConfirm}
        >
          {submitLabel}
        </Button>
      </Stack>
    </Stack>
  )
}

type InviteTeamModalBodyProps = {
  userId: number
}

export const InviteTeamModalBody = ({ userId }: InviteTeamModalBodyProps) => {
  const { data: teams = [], isLoading: isTeamsLoading } = useGetInvitableTeamsQuery()
  const [createInvitation, { isLoading: isSubmitting }] = useCreateTeamInvitationMutation()
  const [selectedId, setSelectedId] = useState<string | number | null>(null)
  const [message, setMessage] = useState("")

  const teamOptions = useMemo(
    () =>
      teams.map((t) => ({
        label: typeof t.title === "string" && t.title.trim().length > 0 ? t.title : `Команда #${t.id}`,
        value: t.id,
      })),
    [teams],
  )

  const handleConfirm = async () => {
    if (selectedId == null || selectedId === "") return
    const teamId = Number(selectedId)
    if (!Number.isFinite(teamId) || teamId <= 0) return

    const trimmed = message.trim()
    try {
      await createInvitation({
        teamId,
        inviteeId: userId,
        ...(trimmed.length > 0 ? { message: trimmed } : {}),
      }).unwrap()
      toast.success("Приглашение отправлено")
      modalEmitter.emitCloseModal()
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? "Не удалось отправить приглашение")
    }
  }

  const loading = isTeamsLoading || isSubmitting

  return (
    <Stack gap={2}>
      <Typography>Вы действительно хотите пригласить пользователя в команду?</Typography>
      <AutocompleteInput
        options={teamOptions}
        value={selectedId ?? ""}
        onChange={(v) => setSelectedId(v ?? null)}
        placeholder="Выберите..."
        fullWidth
        disabled={loading || teamOptions.length === 0}
      />
      <Typography variant="black-14-light">Сообщение (необязательно)</Typography>
      <TextField
        placeholder="Напишите кратко, зачем приглашаете"
        multiline
        minRows={3}
        value={message}
        onChange={(e) => setMessage(e.target.value.slice(0, 500))}
        helperText={`${message.length}/500`}
        disabled={loading}
        fullWidth
      />
      <Stack direction="row" gap={2} justifyContent="flex-end">
        <Button
          variant="action"
          disabled={selectedId == null || selectedId === "" || loading}
          onClick={handleConfirm}
        >
          Пригласить
        </Button>
      </Stack>
    </Stack>
  )
}
